import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpTrayIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getAvailableRoles } from '../../utils/roleUtils';
import { parseCsv } from '../../utils/csv';

/**
 * Employee CSV import (board #950, operator priority 2026-09-19: BUILD it, do not hide the
 * dead control). The Import button was wired to `console.log('Import employees')`.
 *
 * IT INVITES, IT DOES NOT INSERT. Every row goes through the same admin-invite-user edge
 * function the single Add Employee flow uses, one row at a time. That is deliberate and is the
 * only correct option here:
 *   - creating an auth user needs the service-role key, which is server-side only, and
 *     supabase.auth.signUp() on the shared client would clobber the importing owner's own
 *     session (CLAUDE.md §15)
 *   - that function already owns employee numbering, the users + employees + permissions rows,
 *     the invite email, and its own partial-failure reporting
 * A bulk INSERT would be a second implementation of all of it.
 *
 * SEQUENTIAL, not Promise.all. Employee numbers come from a counter the function increments
 * per call, and the invite path also sends mail; firing 50 at once would hammer both for no
 * user-visible gain on an import somebody runs once.
 *
 * PER-ROW RESULTS, no aggregate claim. The edge function returns HTTP 200 while reporting a
 * failed employee row or a failed email in the BODY (board #449 is the same shape), so each
 * row is judged on what came back and the summary counts only what actually landed.
 */

const TEMPLATE_HEADERS = ['First Name', 'Last Name', 'Email', 'Role', 'Phone', 'Job Title', 'Department'];

// The CSV reader lives in utils/csv (board #459) so every importer can read what the
// exporters write. This page's Export quotes a Full Name containing a comma.

const normaliseHeader = (h) => String(h || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

// Accepts this page's own export (Full Name) as well as split name columns.
const HEADER_ALIASES = {
  firstname: 'first_name',
  first: 'first_name',
  givenname: 'first_name',
  lastname: 'last_name',
  last: 'last_name',
  surname: 'last_name',
  fullname: 'full_name',
  name: 'full_name',
  email: 'email',
  emailaddress: 'email',
  role: 'role',
  phone: 'phone',
  phonenumber: 'phone',
  mobile: 'phone',
  jobtitle: 'job_title',
  title: 'job_title',
  department: 'department'
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmployeeImportModal = ({ isOpen, onClose, onImport, existingEmails = [] }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null);
  const fileRef = useRef(null);

  const roleValues = getAvailableRoles().map((r) => r.value);
  const roleLabelToValue = {};
  getAvailableRoles().forEach((r) => { roleLabelToValue[r.label.toLowerCase()] = r.value; });

  // Escape closes, like every other dialog on this page — but never mid-import, because the
  // rows already sent have really been invited and hiding that is not a cancel.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !importing) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, importing, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setRows([]); setFileName(''); setParseError(''); setResults(null);
      setProgress({ done: 0, total: 0 }); setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const takenEmails = new Set(existingEmails.map((e) => String(e || '').trim().toLowerCase()));

  const handleFile = async (file) => {
    setParseError('');
    setResults(null);
    setFileName(file?.name || '');
    if (!file) return;

    let text;
    try {
      text = await file.text();
    } catch (e) {
      setParseError(`Could not read the file: ${e.message}`);
      return;
    }

    const table = parseCsv(text);
    if (table.length < 2) {
      setParseError('That file has a header row but no employees in it.');
      setRows([]);
      return;
    }

    const headerKeys = table[0].map((h) => HEADER_ALIASES[normaliseHeader(h)] || null);
    if (!headerKeys.includes('email')) {
      setParseError('No Email column found. An email address is how an employee is invited, so it is required — use Download template to see the expected columns.');
      setRows([]);
      return;
    }

    const seen = new Set();
    const parsed = table.slice(1).map((cells, idx) => {
      const rec = {};
      headerKeys.forEach((key, c) => { if (key) rec[key] = String(cells[c] ?? '').trim(); });

      // Full Name is what this page exports, so split it rather than rejecting it. Last space
      // wins, which keeps two-word first names in the right field for "Mary Anne Smith".
      if (!rec.first_name && !rec.last_name && rec.full_name) {
        const parts = rec.full_name.split(/\s+/).filter(Boolean);
        rec.first_name = parts.slice(0, -1).join(' ') || parts[0] || '';
        rec.last_name = parts.length > 1 ? parts[parts.length - 1] : '';
      }

      const email = (rec.email || '').toLowerCase();
      const rawRole = (rec.role || '').trim();
      // Accept the label a contractor sees ("Administrator") as well as the stored value.
      const role = roleValues.includes(rawRole.toLowerCase())
        ? rawRole.toLowerCase()
        : (roleLabelToValue[rawRole.toLowerCase()] || '');

      const problems = [];
      if (!email) problems.push('no email');
      else if (!EMAIL_RE.test(email)) problems.push('email does not look like an address');
      else if (takenEmails.has(email)) problems.push('already on your team');
      else if (seen.has(email)) problems.push('repeated in this file');
      if (!rec.first_name) problems.push('no first name');
      if (!rec.last_name) problems.push('no last name');
      if (rawRole && !role) problems.push(`role "${rawRole}" is not one we have`);

      if (email) seen.add(email);

      return {
        line: idx + 2, // +2: 1-based, and the header is line 1 — matches what a spreadsheet shows
        first_name: rec.first_name || '',
        last_name: rec.last_name || '',
        email,
        // Defaulting rather than rejecting: a row that only gives a name and an email is a
        // perfectly ordinary thing to hand an import, and technician is the safest role.
        role: role || 'technician',
        roleDefaulted: !rawRole,
        phone: rec.phone || '',
        job_title: rec.job_title || '',
        department: rec.department || '',
        problems
      };
    });

    setRows(parsed);
  };

  const downloadTemplate = () => {
    const csv = [
      TEMPLATE_HEADERS.join(','),
      '"Sam","Rivera","sam.rivera@example.com","technician","555-0100","Service Technician","Service"'
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const validRows = rows.filter((r) => r.problems.length === 0);
  const badRows = rows.filter((r) => r.problems.length > 0);

  const runImport = async () => {
    setImporting(true);
    setResults(null);
    setProgress({ done: 0, total: validRows.length });

    const outcome = [];
    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      // eslint-disable-next-line no-await-in-loop
      const res = await onImport(row);
      outcome.push({ row, ...res });
      setProgress({ done: i + 1, total: validRows.length });
    }

    setResults(outcome);
    setImporting(false);
  };

  const succeeded = results ? results.filter((r) => r.ok).length : 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-3xl rounded-2xl border border-white/20 bg-slate-900/95 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Import employees</h3>
            <p className="text-sm text-slate-400">Each row is sent an invitation to set their own password.</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            title="Close"
            onClick={onClose}
            disabled={importing}
            className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white disabled:opacity-40"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {!results && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="block w-full max-w-sm cursor-pointer rounded-lg border border-slate-600 bg-slate-800 text-sm text-slate-200 file:mr-3 file:cursor-pointer file:border-0 file:bg-slate-700 file:px-3 file:py-2 file:text-sm file:text-white"
                />
                <button type="button" onClick={downloadTemplate} className="text-sm font-medium text-blue-400 underline decoration-dotted underline-offset-2 hover:text-blue-300">
                  Download template
                </button>
              </div>
              <p className="text-xs text-slate-400">
                Columns: {TEMPLATE_HEADERS.join(', ')}. A single <span className="text-slate-200">Full Name</span> column
                works too, so the file this page exports can be re-imported. Role defaults to Technician when the column is
                missing or blank. Status and Joined are not imported — every imported row starts as a pending invite.
              </p>

              {parseError && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-950/40 p-3 text-sm text-amber-200">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
                  <span>{parseError}</span>
                </div>
              )}

              {rows.length > 0 && (
                <>
                  <div className="text-sm text-slate-300">
                    <span className="font-semibold text-white">{fileName}</span> — {validRows.length} ready to invite
                    {badRows.length > 0 && <span className="text-amber-300">, {badRows.length} will be skipped</span>}
                  </div>

                  <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-700">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-slate-800 text-xs uppercase tracking-wider text-slate-400">
                        <tr>
                          <th className="px-3 py-2 text-left">Line</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Role</th>
                          <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {rows.map((r) => (
                          <tr key={r.line} className={r.problems.length ? 'bg-amber-950/20' : ''}>
                            <td className="px-3 py-2 text-slate-500">{r.line}</td>
                            <td className="px-3 py-2 text-slate-200">{`${r.first_name} ${r.last_name}`.trim() || '—'}</td>
                            <td className="px-3 py-2 text-slate-300">{r.email || '—'}</td>
                            <td className="px-3 py-2 text-slate-300">
                              {r.role}{r.roleDefaulted && <span className="text-slate-500"> (default)</span>}
                            </td>
                            <td className="px-3 py-2">
                              {r.problems.length === 0
                                ? <span className="text-green-400">Ready</span>
                                : <span className="text-amber-300">Skipped — {r.problems.join('; ')}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}

          {results && (
            <>
              <div className="flex items-start gap-2 rounded-lg border border-slate-700 bg-slate-800/60 p-3 text-sm text-slate-200">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
                <span>
                  Invited {succeeded} of {results.length}.
                  {succeeded !== results.length && ' The rest are listed below with the reason — nothing was created for them.'}
                </span>
              </div>
              <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-700">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-slate-800 text-xs uppercase tracking-wider text-slate-400">
                    <tr>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {results.map((r) => (
                      <tr key={r.row.line}>
                        <td className="px-3 py-2 text-slate-300">{r.row.email}</td>
                        <td className="px-3 py-2">
                          {r.ok
                            ? <span className="text-green-400">{r.warning ? `Invited — ${r.warning}` : 'Invited'}</span>
                            : <span className="text-red-400">{r.error || 'Failed'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-white/10 px-5 py-4">
          <div className="text-xs text-slate-400">
            {importing && `Inviting ${progress.done} of ${progress.total}…`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={importing}
              className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-40"
            >
              {results ? 'Done' : 'Cancel'}
            </button>
            {!results && (
              <button
                type="button"
                onClick={runImport}
                disabled={importing || validRows.length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                {importing ? 'Inviting…' : `Invite ${validRows.length} employee${validRows.length === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeImportModal;
